import { supabase } from './supabase';

export interface EmployeeDeduction {
  id: string;
  employee_id: string;
  deduction_type: 'cash_advance' | 'meal' | 'transportation' | 'uniform' | 'equipment' | 'loan' | 'other';
  description?: string;
  amount: number;
  remaining_balance: number;
  installment_amount?: number;
  start_date: string;
  end_date?: string;
  is_recurring: boolean;
  is_active: boolean;
  created_by?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface DeductionCalculation {
  employee_id: string;
  total_deductions: number;
  deductions: {
    id: string;
    type: string;
    description: string;
    amount: number;
    remaining_after: number;
  }[];
}

class DeductionService {
  /**
   * Get all active deductions for an employee
   */
  async getEmployeeDeductions(employeeId: string): Promise<EmployeeDeduction[]> {
    const { data, error } = await supabase
      .from('employee_deductions')
      .select('*')
      .eq('employee_id', employeeId)
      .eq('is_active', true)
      .gt('remaining_balance', 0)
      .order('start_date', { ascending: true });

    if (error) {
      console.error('Error fetching employee deductions:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Calculate deductions for payroll period
   * This processes existing balances and calculates what should be deducted
   */
  async calculatePayrollDeductions(employeeId: string): Promise<DeductionCalculation> {
    const deductions = await this.getEmployeeDeductions(employeeId);
    
    let totalDeductions = 0;
    const processedDeductions: DeductionCalculation['deductions'] = [];

    for (const deduction of deductions) {
      let deductionAmount = 0;
      let remainingAfter = deduction.remaining_balance;

      if (deduction.remaining_balance > 0) {
        if (deduction.installment_amount && deduction.installment_amount > 0) {
          // Use installment amount if specified
          deductionAmount = Math.min(deduction.installment_amount, deduction.remaining_balance);
        } else {
          // If no installment amount, deduct the full remaining balance
          deductionAmount = deduction.remaining_balance;
        }

        remainingAfter = deduction.remaining_balance - deductionAmount;
        totalDeductions += deductionAmount;

        processedDeductions.push({
          id: deduction.id,
          type: deduction.deduction_type,
          description: deduction.description || deduction.deduction_type,
          amount: deductionAmount,
          remaining_after: remainingAfter
        });
      }
    }

    return {
      employee_id: employeeId,
      total_deductions: totalDeductions,
      deductions: processedDeductions
    };
  }

  /**
   * Update deduction balances after payroll processing
   */
  async updateDeductionBalances(deductionCalculations: DeductionCalculation[]): Promise<void> {
    for (const calc of deductionCalculations) {
      for (const deduction of calc.deductions) {
        const { error } = await supabase
          .from('employee_deductions')
          .update({
            remaining_balance: deduction.remaining_after,
            is_active: deduction.remaining_after > 0,
            updated_at: new Date().toISOString()
          })
          .eq('id', deduction.id);

        if (error) {
          console.error(`Error updating deduction ${deduction.id}:`, error);
          throw error;
        }
      }
    }
  }

  /**
   * Get deduction summary for all employees (for payroll generation)
   */
  async getAllEmployeeDeductions(employeeIds: string[]): Promise<DeductionCalculation[]> {
    const calculations: DeductionCalculation[] = [];

    for (const employeeId of employeeIds) {
      const calculation = await this.calculatePayrollDeductions(employeeId);
      calculations.push(calculation);
    }

    return calculations;
  }

  /**
   * Create a new deduction for an employee
   */
  async createDeduction(deduction: Omit<EmployeeDeduction, 'id' | 'created_at' | 'updated_at'>): Promise<EmployeeDeduction> {
    const { data, error } = await supabase
      .from('employee_deductions')
      .insert({
        ...deduction,
        remaining_balance: deduction.remaining_balance || deduction.amount
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating deduction:', error);
      throw error;
    }

    return data;
  }

  /**
   * Update an existing deduction
   */
  async updateDeduction(id: string, updates: Partial<EmployeeDeduction>): Promise<EmployeeDeduction> {
    const { data, error } = await supabase
      .from('employee_deductions')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating deduction:', error);
      throw error;
    }

    return data;
  }

  /**
   * Delete a deduction
   */
  async deleteDeduction(id: string): Promise<void> {
    const { error } = await supabase
      .from('employee_deductions')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting deduction:', error);
      throw error;
    }
  }
}

export const deductionService = new DeductionService();